import React from "react";
// Sidebar.jsx -- Sprint Hub LAY01v1
// V3: Permissions-filtered agents, agent drill-in state, storefronts section,
//     conversations/work links, agent settings at bottom.
// HOME: pinned agents (filtered by canView) + storefronts + convos/work + settings
// AGENT DRILL-IN: back button + agent overview links + agent settings
// ASCII only. No Unicode. var declarations only.
import { useState, useEffect } from 'react';
import { useTheme, FONT, MONO } from '../lib/theme.js';
import { usePermissions } from '../lib/permissions.js';
import { labelFor } from '../lib/humanize.js';
import HubLogo from './HubLogo.jsx';

// Maps sidebar_order_json IDs to domain objects.
// agentId = null means not filtered by permissions (always shown if in prefs).
var DOMAIN_MAP = {
  'brief':        { id: 'brief',        label: 'Brief',          icon: 'brief',        route: '/brief',                           agentId: null },
  'pipeline':     { id: 'pipeline',     label: 'Pipeline',       icon: 'crm',          route: '/agent/agent_sales_director',      agentId: 'agent_sales_director' },
  'crm':          { id: 'crm',          label: 'Pipeline',       icon: 'crm',          route: '/agent/agent_sales_director',      agentId: 'agent_sales_director' },
  'clients':      { id: 'clients',      label: 'Clients',        icon: 'crm',          route: '/clients',                         agentId: 'agent_sales_director' },
  'portfolio':    { id: 'portfolio',    label: 'Portfolio',      icon: 'folder',       route: '/portfolio',                       agentId: null },
  'projects':     { id: 'projects',     label: 'Projects',       icon: 'folder',       route: '/projects',     agentId: null },
  'cfo':          { id: 'cfo',          label: 'CFO',            icon: 'dollar',       route: '/agent/agent_cfo',                 agentId: 'agent_cfo' },
  'finance':      { id: 'finance',      label: 'CFO',            icon: 'dollar',       route: '/agent/agent_cfo',                 agentId: 'agent_cfo' },
  'finance-ops':  { id: 'finance-ops',  label: 'AP/AR',          icon: 'dollar',       route: '/finance-ops',                     agentId: null },
  'books':        { id: 'books',        label: 'Bookkeeper',     icon: 'ledger',       route: '/agent/agent_bookkeeper?section=gl', agentId: 'agent_bookkeeper' },
  'decisions':    { id: 'decisions',    label: 'Governance',     icon: 'approvals',    route: '/agent/agent_governance',          agentId: 'agent_governance' },
  'approvals':    { id: 'approvals',    label: 'Governance',     icon: 'approvals',    route: '/agent/agent_governance',          agentId: 'agent_governance' },
  'hr-team':      { id: 'hr-team',      label: 'HR & Team',      icon: 'hr-team',      route: '/agent/agent_people_hr',           agentId: 'agent_people_hr' },
  'investor-hub': { id: 'investor-hub', label: 'IR',             icon: 'investor-hub', route: '/agent/agent_ir',                  agentId: 'agent_ir' },
  'payroll':      { id: 'payroll',      label: 'Payroll',        icon: 'dollar',       route: '/agent/agent_payroll',             agentId: 'agent_payroll' },
  'platform-ops': { id: 'platform-ops', label: 'Connectors',    icon: 'plug',         route: '/connectors',                      agentId: 'agent_coo' },
  'connectors':   { id: 'connectors',   label: 'Connectors',     icon: 'plug',         route: '/connectors',                      agentId: 'agent_coo' },
  'jockey':       { id: 'jockey',       label: 'Jockey',         icon: 'jockey',       route: '/agent/agent_engineering_manager', agentId: 'agent_engineering_manager' },
  'fundraise':    { id: 'fundraise',    label: 'Fundraise',      icon: 'bar',          route: '/fundraise',                       agentId: 'agent_fundraise' },
  'calls':        { id: 'calls',        label: 'Calls',          icon: 'phone',        route: '/calls',                           agentId: 'agent_call' },
  'delivery':     { id: 'delivery',     label: 'Delivery',       icon: 'folder',       route: '/agent/agent_engineering_manager', agentId: 'agent_engineering_manager' },
  'engineering':  { id: 'engineering',  label: 'Engineering',    icon: 'bolt',         route: '/agent/agent_engineering_manager', agentId: 'agent_engineering_manager' },
  'coo':          { id: 'coo',          label: 'COO',            icon: 'gear',         route: '/agent/agent_coo',                 agentId: 'agent_coo' },
  'automation':   { id: 'automation',   label: 'Automation',     icon: 'bolt',         route: '/agent/agent_coo',                 agentId: 'agent_coo' },
  'vendors':      { id: 'vendors',      label: 'Vendors',        icon: 'gear',         route: '/agent/agent_coo',                 agentId: 'agent_coo' },
  'lp-pipeline':  { id: 'lp-pipeline',  label: 'LP Pipeline',    icon: 'bar',          route: '/lp-pipeline',                     agentId: null },
  'fund':         { id: 'fund',         label: 'Sprint Capital', icon: 'bar',          route: '/agent/agent_sales_director',      agentId: 'agent_sales_director' },
  'fund-counsel': { id: 'fund-counsel', label: 'Fund Counsel',   icon: 'approvals',    route: '/agent/agent_fund_counsel',        agentId: 'agent_fund_counsel' },
  'contract':     { id: 'contract',     label: 'Contract',       icon: 'briefcase',    route: '/agent/agent_contract',            agentId: 'agent_contract' },
  'superinsight': { id: 'superinsight', label: 'Super Insight',  icon: 'eye',          route: '/brief',                           agentId: null },
  'labs':         { id: 'labs',         label: 'Labs',           icon: 'flask',        route: '/labs',                            agentId: null },
  'vault':        { id: 'vault',        label: 'Vault',          icon: 'lock',         route: '/vault',                           agentId: null },
  'investment-committee': { id: 'investment-committee', label: 'IC',           icon: 'approvals',    route: '/investment-committee', agentId: null },
  'agency':               { id: 'agency',               label: 'Agency',       icon: 'briefcase',    route: '/agency',              agentId: null },
  'agent-network':        { id: 'agent-network',        label: 'Agent Network',icon: 'network',      route: '/agent-network',       agentId: null },
  'narrative':            { id: 'narrative',            label: 'Narrative',    icon: 'brief',        route: '/narrative',           agentId: null },
  'admin':                { id: 'admin',                label: 'Admin',        icon: 'gear',         route: '/admin',               agentId: null },
  'clone':                { id: 'clone',                label: 'Clone',        icon: 'people',       route: '/clone',               agentId: null },
  'reconciliation':       { id: 'reconciliation',       label: 'Reconciliation', icon: 'ledger',      route: '/agent/agent_bookkeeper',       agentId: 'agent_bookkeeper' },
  'storefronts':          { id: 'storefronts',          label: 'Storefronts',  icon: 'briefcase',    route: '/agent/agent_storefront_scout', agentId: 'agent_storefront_scout' },
  'org':                  { id: 'org',                  label: 'Organization', icon: 'gear',         route: '/org',                          agentId: 'agent_org_admin' },
  'shadow_claude':      { id: 'shadow_claude',      label: 'Claude Opus', icon: 'bolt', route: '/agent/shadow_claude',      agentId: 'shadow_claude' },
  'shadow_gpt':         { id: 'shadow_gpt',         label: 'GPT-4o',      icon: 'bolt', route: '/agent/shadow_gpt',         agentId: 'shadow_gpt' },
  'shadow_gemini':      { id: 'shadow_gemini',      label: 'Gemini Pro',  icon: 'bolt', route: '/agent/shadow_gemini',      agentId: 'shadow_gemini' },
  'shadow_perplexity':  { id: 'shadow_perplexity',  label: 'Perplexity',  icon: 'bolt', route: '/agent/shadow_perplexity',  agentId: 'shadow_perplexity' },
  'hub':                { id: 'hub',                label: 'Hub',         icon: 'brief', route: '/hub',                     agentId: 'agent_hub' },
  'hc-auditor':         { id: 'hc-auditor',         label: 'HC-Auditor',  icon: 'shield', route: '/agent/agent_hc_auditor', agentId: 'agent_hc_auditor' },
  'hc-builder':         { id: 'hc-builder',         label: 'HC-Builder',  icon: 'bolt',  route: '/agent/agent_hc_builder',  agentId: 'agent_hc_builder' },
  'shadow':             { id: 'shadow',             label: 'Models',      icon: 'flask', route: '/agent/shadow_claude',     agentId: 'shadow_claude' },
  'pricing-admin':      { id: 'pricing-admin',      label: 'Pricing',     icon: 'dollar', route: '/pricing-admin',          agentId: 'agent_pricing_intel' },
};

// C26-S17v1: Items that must NEVER appear in sidebar regardless of config
var SIDEBAR_EXCLUDE = ['settings', 'org', 'organization', 'chat', 'clone', 'all-clones', 'storefront_scout', 'storefronts', 'agent_chat', 'agent_storefront_scout', 'agent_aaron_clone', 'agent_hugh_clone', 'agent_clayton_clone', 'agent_dani_clone', 'agent_eugenio_clone'];

// Plan gates: domain id -> minimum plan required
var PLAN_GATES = {
  'delivery':    'scale',
  'storefronts': 'scale',
  'agency':      'scale',
  'compliance':  'enterprise',
};

var PLAN_RANK = { 'start': 0, 'scale': 1, 'enterprise': 2 };

function planMeetsGate(userPlan, requiredPlan) {
  var rank = PLAN_RANK[userPlan] !== undefined ? PLAN_RANK[userPlan] : 0;
  var req = PLAN_RANK[requiredPlan] !== undefined ? PLAN_RANK[requiredPlan] : 0;
  return rank >= req;
}

// Agent display info for drill-in state header + any agent not in DOMAIN_MAP
var AGENT_DISPLAY = {
  // -- core --
  'agent_cfo':                    { label: 'CFO',              icon: 'dollar' },
  'agent_bookkeeper':             { label: 'Bookkeeper',       icon: 'ledger' },
  'agent_collections':            { label: 'Collections',      icon: 'dollar' },
  'agent_payroll':                { label: 'Payroll',          icon: 'dollar' },
  'agent_chief_of_staff':         { label: 'Chief of Staff',   icon: 'brief' },
  'agent_coo':                    { label: 'Platform Ops',     icon: 'gear' },
  'agent_governance':             { label: 'Decisions',        icon: 'approvals' },
  // -- growth --
  'agent_sales_director':         { label: 'Pipeline',         icon: 'crm' },
  'agent_sdr':                    { label: 'Outbound',         icon: 'outbox' },
  'agent_fundraise':              { label: 'Fundraise',        icon: 'bar' },
  'agent_ir':                     { label: 'IR',               icon: 'investor-hub' },
  'agent_networking':             { label: 'Networking',       icon: 'people' },
  'agent_account_manager':        { label: 'Account Mgr',      icon: 'briefcase' },
  'agent_customer_success':       { label: 'Client Success',   icon: 'hr-team' },
  'agent_pitch_deck':             { label: 'Pitch Deck',       icon: 'bar' },
  // -- engineering --
  'agent_engineering_manager':    { label: 'Engineering',      icon: 'bolt' },
  'agent_evolver':                { label: 'Evolver',          icon: 'jockey' },
  'agent_cracked':                { label: 'Sleuth',           icon: 'eye' },
  // -- legal --
  'agent_fund_counsel':           { label: 'Fund Counsel',     icon: 'approvals' },
  'agent_contract':               { label: 'Contracts',        icon: 'ledger' },
  'agent_legal':                  { label: 'General Counsel',  icon: 'approvals' },
  // -- fund_operations --
  'agent_investment_committee':   { label: 'Investment Cmte',  icon: 'bar' },
  'agent_capital_call':           { label: 'Capital Call',     icon: 'dollar' },
  'agent_lp_relations':           { label: 'LP Relations',     icon: 'people' },
  'agent_digital_lpac':           { label: 'Digital LPAC',     icon: 'people' },
  'agent_technical_dd':           { label: 'Technical DD',     icon: 'bolt' },
  'agent_post_merger':            { label: 'Post-Merger',      icon: 'briefcase' },
  'agent_talent_assessment':      { label: 'Talent',           icon: 'people' },
  'agent_synergy_calculator':     { label: 'Synergy Calc',     icon: 'bar' },
  'agent_portfolio_value':        { label: 'Portfolio Value',  icon: 'dollar' },
  'agent_company_benchmark':      { label: 'Benchmarks',       icon: 'bar' },
  'agent_notary':                 { label: 'Notary',           icon: 'approvals' },
  'agent_portfolio_monitor':      { label: 'Portfolio Monitor',icon: 'bar' },
  // -- communication --
  'agent_call':                   { label: 'Calls',            icon: 'phone' },
  'agent_calendar':               { label: 'Calendar Mgr',     icon: 'briefcase' },
  'agent_email':                  { label: 'Email',            icon: 'inbox' },
  'agent_chat':                   { label: 'Chat',             icon: 'message' },
  // -- security --
  'agent_security_monitor':       { label: 'Security',         icon: 'shield' },
  'agent_red_team':               { label: 'Red Team',         icon: 'shield' },
  'agent_soc2':                   { label: 'SOC 2',            icon: 'shield' },
  'agent_fraud_detection':        { label: 'Fraud Detection',  icon: 'shield' },
  'agent_ai_model_security':      { label: 'AI Security',      icon: 'shield' },
  'agent_platform_governance':    { label: 'Governance',       icon: 'shield' },
  // -- compliance --
  'agent_hipaa':                  { label: 'HIPAA',            icon: 'shield' },
  'agent_phi_detection':          { label: 'PHI Detection',    icon: 'shield' },
  'agent_hipaa_audit':            { label: 'HIPAA Audit',      icon: 'approvals' },
  'agent_fedramp':                { label: 'FedRAMP',          icon: 'approvals' },
  'agent_gov_contract':           { label: 'Gov Contracts',    icon: 'approvals' },
  'agent_hr_compliance':          { label: 'HR Compliance',    icon: 'approvals' },
  'agent_benefits_leave':         { label: 'Benefits & Leave', icon: 'approvals' },
  'agent_intl_employment':        { label: 'Intl Employment',  icon: 'approvals' },
  // -- content --
  'agent_content':                { label: 'Content',          icon: 'folder' },
  'agent_narrative':              { label: 'Narrative',        icon: 'folder' },
  'agent_gtm':                    { label: 'GTM',              icon: 'folder' },
  // -- infrastructure --
  'agent_upgrade':                { label: 'Upgrade',          icon: 'bolt' },
  'agent_connector':              { label: 'Connectors',       icon: 'plug' },
  'agent_onboarding':             { label: 'Onboarding',       icon: 'brief' },
  // -- clones --
  'agent_aaron_clone':            { label: 'Aaron Clone',      icon: 'people' },
  'agent_hugh_clone':             { label: 'Hugh Clone',       icon: 'people' },
  'agent_clayton_clone':          { label: 'Clayton Clone',    icon: 'people' },
  'agent_dani_clone':             { label: 'Dani Clone',       icon: 'people' },
  'agent_eugenio_clone':          { label: 'Eugenio Clone',    icon: 'people' },
  // -- storefront --
  'agent_growth':                 { label: 'Growth',           icon: 'bar' },
  'agent_storefront_scout':       { label: 'Storefront Scout', icon: 'briefcase' },
  'agent_privacy':                { label: 'Privacy',          icon: 'shield' },
  // -- personal_infrastructure --
  'agent_hub_personal':           { label: 'Hub',              icon: 'brief' },
  // -- personal_core --
  'agent_household_cfo':          { label: 'Household CFO',    icon: 'dollar' },
  'agent_obligations':            { label: 'Obligations',      icon: 'approvals' },
  'agent_subscription_manager':   { label: 'Subscriptions',    icon: 'dollar' },
  // -- personal_growth --
  'agent_investment_monitor':     { label: 'Investments',      icon: 'bar' },
  'agent_real_estate_sleuth':     { label: 'RE Sleuth',        icon: 'bar' },
  // -- personal_legal --
  'agent_entity_manager':         { label: 'Entity Manager',   icon: 'approvals' },
  'agent_estate_legacy':          { label: 'Estate & Legacy',  icon: 'approvals' },
  'agent_notary_personal':        { label: 'Notary',           icon: 'approvals' },
  // -- personal_communication --
  'agent_family_calendar':        { label: 'Family Calendar',  icon: 'briefcase' },
  // -- personal_security --
  'agent_security_monitor_personal':  { label: 'Security',     icon: 'shield' },
  'agent_fraud_detection_personal':   { label: 'Fraud Alert',  icon: 'shield' },
  'agent_ai_model_security_personal': { label: 'AI Security',  icon: 'shield' },
  // -- personal_health --
  'agent_family_health':          { label: 'Family Health',    icon: 'people' },
  'agent_fitness_coach':          { label: 'Fitness Coach',    icon: 'people' },
  // -- personal_lifestyle --
  'agent_travel':                 { label: 'Travel',           icon: 'briefcase' },
  'agent_couch_potato':           { label: 'Couch Potato',     icon: 'briefcase' },
  'agent_gift_occasion':          { label: 'Gifts & Occasions',icon: 'briefcase' },
  'agent_kids_family_fun':        { label: 'Family Fun',       icon: 'people' },
  'agent_education':              { label: 'Education',        icon: 'folder' },
  // -- personal_property --
  'agent_property_manager':       { label: 'Property Mgr',     icon: 'briefcase' },
  'agent_home_maintenance':       { label: 'Home Maint.',      icon: 'briefcase' },
  'agent_yard_pro':               { label: 'Yard Pro',         icon: 'briefcase' },
  'agent_vehicle':                { label: 'Vehicle',          icon: 'briefcase' },
  // -- misc --
  'agent_cpa':                    { label: 'CPA / Tax',        icon: 'dollar' },
  'agent_vault':                  { label: 'Vault',            icon: 'ledger' },
  'agent_people_hr':              { label: 'HR & Team',        icon: 'hr-team' },
};

// Keyboard shortcuts shown next to sidebar items (Cmd+N)
var SHORTCUTS = {
  'cfo':      '1', 'finance':   '1',
  'books':    '2',
  'crm':      '3', 'pipeline':  '3',
  'jockey':   '4',
  'hr-team':  '5',
};

// Maps domain IDs to agentId for status badge lookup
var DOMAIN_AGENT_MAP = {
  'brief':        'agent_chief_of_staff',
  'cfo':          'agent_cfo', 'finance':     'agent_cfo',
  'books':        'agent_bookkeeper', 'reconciliation': 'agent_bookkeeper',
  'decisions':    'agent_governance', 'approvals':      'agent_governance',
  'hr-team':      'agent_people_hr',
  'jockey':       'agent_engineering_manager',
  'connectors':   'agent_coo', 'platform-ops': 'agent_coo',
  'fundraise':    'agent_fundraise',
  'calls':        'agent_call',
  'investor-hub': 'agent_ir',
  'crm':          'agent_sales_director', 'pipeline': 'agent_sales_director',
};

// Storefronts shown in sidebar home state
var STOREFRONTS = [
  { id: 'bookkeeperos', label: 'BookkeeperOS', agent: 'agent_bookkeeper', color: '#14B8A6' },
  { id: 'collectos',    label: 'CollectOS',    agent: 'agent_collections', color: '#14B8A6' },
];

// Agent drill-in: which storefronts to show below separator
var AGENT_STOREFRONT_MAP = {
  'agent_bookkeeper':  ['bookkeeperos'],
  'agent_collections': ['collectos'],
};

// Per-storefront sidebar sections shown in storefront flip mode
var STOREFRONT_SIDEBAR_SECTIONS = {
  bookkeeperos: [
    { id: 'dashboard',  label: 'Dashboard',    icon: 'bar' },
    { id: 'customers',  label: 'Customers',    icon: 'people' },
    { id: 'revenue',    label: 'Revenue',      icon: 'dollar' },
    { id: 'pricing',    label: 'Pricing',      icon: 'queue' },
    { id: 'landing',    label: 'Landing Page', icon: 'briefcase' },
    { id: 'config',     label: 'Configuration', icon: 'settings' },
  ],
  collectos: [
    { id: 'dashboard',  label: 'Dashboard',    icon: 'bar' },
    { id: 'customers',  label: 'Customers',    icon: 'people' },
    { id: 'pricing',    label: 'Pricing',      icon: 'queue' },
    { id: 'config',     label: 'Configuration', icon: 'settings' },
  ],
};

// Per-agent sidebar sections shown in drill-in state
var AGENT_SIDEBAR_SECTIONS = {
  agent_cfo: [
    { id: 'financials',label: 'Financials',icon: 'dollar' },
    { id: 'scenarios', label: 'Scenarios', icon: 'bar' },
    { id: 'reports',   label: 'Reports',   icon: 'folder' },
    { id: 'artifacts', label: 'Artifacts', icon: 'folder' },
  ],
  agent_bookkeeper: [
    { id: 'reconciliation',label: 'Reconciliation',    icon: 'approvals' },
    { id: 'gl',            label: 'General Ledger',    icon: 'ledger' },
    { id: 'transactions',  label: 'Transactions',      icon: 'queue' },
    { id: 'coa',           label: 'Chart of Accounts', icon: 'ledger' },
    { id: 'monthly_close', label: 'Monthly Close',     icon: 'briefcase' },
    { id: 'artifacts',     label: 'Artifacts',         icon: 'folder' },
  ],
  agent_sales_director: [
    { id: 'pipeline',  label: 'Pipeline',  icon: 'funnel' },
    { id: 'investors', label: 'Investors', icon: 'investor-hub' },
    { id: 'clients',   label: 'Clients',   icon: 'people' },
    { id: 'artifacts', label: 'Artifacts', icon: 'folder' },
  ],
  agent_collections: [
    { id: 'ar_aging',  label: 'AR Aging',      icon: 'queue' },
    { id: 'dunning',   label: 'Dunning Queue', icon: 'inbox' },
    { id: 'artifacts', label: 'Artifacts',     icon: 'folder' },
  ],
  agent_chief_of_staff: [
    { id: 'priorities', label: 'Priorities', icon: 'brief' },
    { id: 'tasks',      label: 'Tasks',      icon: 'approvals' },
    { id: 'decisions',  label: 'Decisions',  icon: 'shield' },
    { id: 'calendar',   label: 'Calendar',   icon: 'briefcase' },
    { id: 'artifacts',  label: 'Artifacts',  icon: 'folder' },
  ],
  agent_engineering_manager: [
    { id: 'sessions',  label: 'Sessions',  icon: 'bolt' },
    { id: 'skills',    label: 'Skills',    icon: 'gear' },
    { id: 'knowledge', label: 'Knowledge', icon: 'brief' },
    { id: 'prds',      label: 'PRDs',      icon: 'folder' },
    { id: 'artifacts', label: 'Artifacts', icon: 'folder' },
  ],
  agent_ir: [
    { id: 'roster',     label: 'Investors',  icon: 'investor-hub' },
    { id: 'pipeline',   label: 'Pipeline',   icon: 'funnel' },
    { id: 'cap_table',  label: 'Cap Table',  icon: 'ledger' },
    { id: 'portfolio',  label: 'Portfolio',  icon: 'folder' },
    { id: 'calendar',   label: 'Calendar',   icon: 'calendar' },
    { id: 'content',    label: 'Content',    icon: 'brief' },
    { id: 'docs',       label: 'Materials',  icon: 'folder' },
    { id: 'engagement', label: 'Engagement', icon: 'eye' },
    { id: 'campaigns',  label: 'Campaigns',  icon: 'bar' },
  ],
  agent_fundraise: [
    { id: 'pipeline',  label: 'Pipeline',  icon: 'funnel' },
    { id: 'investors', label: 'Investors', icon: 'investor-hub' },
    { id: 'artifacts', label: 'Artifacts', icon: 'folder' },
  ],
  agent_customer_success: [
    { id: 'health',    label: 'Client Health', icon: 'hr-team' },
    { id: 'migration', label: 'Migration',     icon: 'bolt' },
    { id: 'artifacts', label: 'Artifacts',     icon: 'folder' },
  ],
  agent_evolver: [
    { id: 'health',    label: 'Agent Health', icon: 'bar' },
    { id: 'audits',    label: 'Audits',       icon: 'brief' },
    { id: 'artifacts', label: 'Artifacts',    icon: 'folder' },
  ],
  agent_call: [
    { id: 'upcoming',    label: 'Upcoming',    icon: 'briefcase' },
    { id: 'transcripts', label: 'Transcripts', icon: 'folder' },
    { id: 'artifacts',   label: 'Artifacts',   icon: 'folder' },
  ],
  agent_growth: [
    { id: 'storefronts', label: 'Storefronts', icon: 'briefcase' },
    { id: 'customers',   label: 'Customers',   icon: 'people' },
    { id: 'artifacts',   label: 'Artifacts',   icon: 'folder' },
  ],
  agent_coo: [
    { id: 'connectors', label: 'Connectors',     icon: 'plug' },
    { id: 'saas',       label: 'SaaS Inventory', icon: 'ledger' },
    { id: 'artifacts',  label: 'Artifacts',      icon: 'folder' },
  ],
  agent_sdr: [
    { id: 'sequences', label: 'Outreach',  icon: 'outbox' },
    { id: 'prospects', label: 'Prospects', icon: 'eye' },
    { id: 'artifacts', label: 'Artifacts', icon: 'folder' },
  ],
  agent_people_hr: [
    { id: 'team',      label: 'Team',      icon: 'people' },
    { id: 'artifacts', label: 'Artifacts', icon: 'folder' },
  ],
  agent_contract: [
    { id: 'contracts', label: 'Contracts', icon: 'folder' },
    { id: 'renewals',  label: 'Renewals',  icon: 'gear' },
    { id: 'artifacts', label: 'Artifacts', icon: 'folder' },
  ],
  agent_governance: [
    { id: 'decisions', label: 'Decisions', icon: 'shield' },
    { id: 'artifacts', label: 'Artifacts', icon: 'folder' },
  ],
  agent_fund_counsel: [
    { id: 'open_items',  label: 'Open Items',  icon: 'approvals' },
    { id: 'compliance',  label: 'Compliance',  icon: 'shield' },
    { id: 'instruments', label: 'Instruments', icon: 'ledger' },
    { id: 'documents',   label: 'Documents',   icon: 'folder' },
    { id: 'artifacts',   label: 'Artifacts',   icon: 'folder' },
  ],
  agent_networking: [
    { id: 'contacts',  label: 'Contacts',  icon: 'people' },
    { id: 'reconnect', label: 'Reconnect', icon: 'network' },
    { id: 'events',    label: 'Events',    icon: 'briefcase' },
    { id: 'artifacts', label: 'Artifacts', icon: 'folder' },
  ],
  agent_narrative: [
    { id: 'stories',   label: 'Stories',   icon: 'folder' },
    { id: 'audiences', label: 'Audiences', icon: 'people' },
    { id: 'faqs',      label: 'FAQs',      icon: 'queue' },
    { id: 'artifacts', label: 'Artifacts', icon: 'folder' },
  ],
  agent_payroll: [
    { id: 'roster',    label: 'Roster',    icon: 'people' },
    { id: 'invoices',  label: 'Invoices',  icon: 'ledger' },
    { id: 'schedule',  label: 'Schedule',  icon: 'briefcase' },
    { id: 'artifacts', label: 'Artifacts', icon: 'folder' },
  ],
  agent_legal: [
    { id: 'policies',  label: 'Policies',  icon: 'shield' },
    { id: 'reviews',   label: 'Reviews',   icon: 'approvals' },
    { id: 'artifacts', label: 'Artifacts', icon: 'folder' },
  ],
  agent_platform_governance: [
    { id: 'proposals',  label: 'Proposals',  icon: 'approvals' },
    { id: 'violations', label: 'Violations', icon: 'shield' },
    { id: 'scans',      label: 'Scans',      icon: 'eye' },
    { id: 'artifacts',  label: 'Artifacts',  icon: 'folder' },
  ],
  agent_hc_builder: [
    { id: 'roadmap',   label: 'Roadmap',          icon: 'bar' },
    { id: 'bugs',      label: 'Bugs & Features',  icon: 'inbox' },
    { id: 'testing',   label: 'Testing',          icon: 'approvals' },
    { id: 'hardening', label: 'Hardening',        icon: 'shield' },
    { id: 'notepad',   label: 'Notepad',          icon: 'brief' },
    { id: 'artifacts', label: 'Artifacts',        icon: 'folder' },
  ],
  agent_hc_auditor: [
    { id: 'findings',  label: 'Findings',     icon: 'shield' },
    { id: 'history',   label: 'Audit History',icon: 'brief' },
    { id: 'artifacts', label: 'Artifacts',    icon: 'folder' },
  ],
  _default: [
    { id: 'artifacts', label: 'Artifacts', icon: 'folder' },
  ],
};

// Read section query param from the current URL
function getHashSection() {
  try {
    return new URLSearchParams(window.location.search || '').get('section') || '';
  } catch(e) { return ''; }
}

function parseDomains(prefs) {
  if (prefs && prefs.sidebar_order_json) {
    try {
      var ids = typeof prefs.sidebar_order_json === 'string'
        ? JSON.parse(prefs.sidebar_order_json)
        : prefs.sidebar_order_json;
      if (Array.isArray(ids) && ids.length > 0) {
        var mapped = [];
        ids.forEach(function(id) {
          if (id === 'shadow') {
            // Expand shadow group into all 4 shadow agents
            ['shadow_claude', 'shadow_gpt', 'shadow_gemini', 'shadow_perplexity'].forEach(function(sid) {
              if (DOMAIN_MAP[sid]) mapped.push(DOMAIN_MAP[sid]);
            });
          } else if (DOMAIN_MAP[id] && SIDEBAR_EXCLUDE.indexOf(id) < 0) {
            mapped.push(DOMAIN_MAP[id]);
          }
        });
        if (mapped.length > 0) return mapped;
      }
    } catch(e) {}
  }
  return [DOMAIN_MAP['brief']];
}

function isActive(route, currentPath) {
  if (!route) return false;
  if (route === '/' || route === '/brief') {
    return currentPath === '/' || currentPath === '/brief';
  }
  return currentPath === route || currentPath.startsWith(route + '/');
}

// All SVG icons: 18x18, viewBox 0 0 24 24, stroke, strokeWidth 1.8
function NavIcon(props) {
  var name = props.name;
  var color = props.color;
  var p = {
    width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none',
    stroke: color, strokeWidth: '1.8', strokeLinecap: 'round', strokeLinejoin: 'round'
  };
  switch (name) {
    case 'brief':
      return (<svg {...p}><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="7" y1="8" x2="17" y2="8"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="7" y1="16" x2="17" y2="16"/></svg>);
    case 'dollar':
      return (<svg {...p}><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>);
    case 'people':
      return (<svg {...p}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>);
    case 'bar':
      return (<svg {...p}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>);
    case 'shield':
      return (<svg {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>);
    case 'bolt':
      return (<svg {...p}><polygon points="13,2 3,14 12,14 11,22 21,10 12,10"/></svg>);
    case 'phone':
      return (<svg {...p}><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 014.69 12 19.79 19.79 0 011.61 3.4 2 2 0 013.6 1.22h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L7.91 9.91a16 16 0 006.18 6.18l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>);
    case 'flask':
      return (<svg {...p}><path d="M9 3h6"/><path d="M10 3v6.5L5 19a1 1 0 00.9 1.5h12.2A1 1 0 0019 19l-5-9.5V3"/></svg>);
    case 'eye':
      return (<svg {...p}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>);
    case 'funnel':
      return (<svg {...p}><polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46"/></svg>);
    case 'folder':
      return (<svg {...p}><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>);
    case 'plug':
      return (<svg {...p}><path d="M12 22v-5"/><path d="M9 7V3M15 7V3"/><rect x="5" y="7" width="14" height="8" rx="2"/></svg>);
    case 'gear':
    case 'settings':
      return (<svg {...p}><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg>);
    case 'inbox':
      return (<svg {...p}><polyline points="22,12 16,12 14,15 10,15 8,12 2,12"/><path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/></svg>);
    case 'outbox':
      return (<svg {...p}><polyline points="22,12 16,12 14,15 10,15 8,12 2,12"/><path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/><polyline points="9,5 12,2 15,5"/><line x1="12" y1="2" x2="12" y2="9"/></svg>);
    case 'ledger':
      return (<svg {...p}><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="9" x2="9" y2="21"/></svg>);
    case 'finance':
      return (<svg {...p}><path d="M12 2v20"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>);
    case 'crm':
      return (<svg {...p}><circle cx="9" cy="7" r="3.5"/><path d="M1 21c0-4.4 3.6-8 8-8s8 3.6 8 8"/><circle cx="18" cy="8" r="2.5"/><path d="M16.5 21c.3-3 2.5-5.5 5.5-5.5"/></svg>);
    case 'projects':
      return (<svg {...p}><path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"/></svg>);
    case 'approvals':
      return (<svg {...p}><path d="M12 2L4 6v6c0 5.5 3.6 10.7 8 12 4.4-1.3 8-6.5 8-12V6L12 2z"/><polyline points="9 12 11 14 15 10"/></svg>);
    case 'jockey':
      return (<svg {...p}><path d="M13 2L4.5 13H12L11 22l8.5-11H12L13 2z"/></svg>);
    case 'hr-team':
      return (<svg {...p}><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>);
    case 'platform-ops':
      return (<svg {...p}><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg>);
    case 'investor-hub':
      return (<svg {...p}><rect x="3" y="12" width="4" height="9"/><rect x="10" y="7" width="4" height="14"/><rect x="17" y="3" width="4" height="18"/></svg>);
    case 'lock':
      return (<svg {...p}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>);
    case 'briefcase':
      return (<svg {...p}><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/><line x1="12" y1="12" x2="12" y2="12"/><line x1="2" y1="12" x2="22" y2="12"/></svg>);
    case 'network':
      return (<svg {...p}><circle cx="12" cy="12" r="2.5"/><circle cx="4" cy="7" r="1.8"/><circle cx="20" cy="7" r="1.8"/><circle cx="4" cy="17" r="1.8"/><circle cx="20" cy="17" r="1.8"/><line x1="6" y1="8" x2="10" y2="11"/><line x1="18" y1="8" x2="14" y2="11"/><line x1="6" y1="16" x2="10" y2="13"/><line x1="18" y1="16" x2="14" y2="13"/></svg>);
    case 'message':
      return (<svg {...p}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>);
    case 'queue':
      return (<svg {...p}><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>);
    case 'bell':
      return (<svg {...p}><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>);
    case 'arrow-left':
      return (<svg {...p}><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>);
    case 'settings-sliders':
      return (<svg {...p}><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>);
    default:
      return (<svg {...p}><circle cx="12" cy="12" r="8"/></svg>);
  }
}

function NavItem(props) {
  var item = props.item;
  var active = props.active;
  var onClick = props.onClick;
  var theme = props.theme;
  var statusDot = props.statusDot;
  var statusCount = props.statusCount;
  var statusTooltip = props.statusTooltip;
  var muted = props.muted;
  var onPin = props.onPin;
  var isPinned = props.isPinned;
  var locked = props.locked || false;
  var gate = props.gate || null;
  var _hover = useState(false);
  var hovered = _hover[0]; var setHovered = _hover[1];

  var iconColor = active ? theme.accent : (muted ? theme.textMuted : theme.sidebarText);
  var iconOpacity = active ? 1.0 : (muted ? 0.35 : 0.5);
  var bg = active ? theme.sidebarActiveBg : (hovered ? theme.sidebarHover : 'transparent');
  var textColor = active ? theme.sidebarTextActive : (muted ? theme.textMuted : theme.sidebarText);
  var fontWeight = active ? 600 : 400;

  return (
    <button
      onClick={function() { if (onClick) onClick(locked ? '/agent/agent_org_admin?section=billing' : item.route); }}
      onMouseEnter={function() { setHovered(true); }}
      onMouseLeave={function() { setHovered(false); }}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
        padding: '8px 12px', borderRadius: 8, border: 'none',
        cursor: 'pointer', background: bg, color: textColor,
        fontWeight: fontWeight, fontSize: 12.5,
        fontFamily: FONT,
        textAlign: 'left', transition: 'background 0.12s',
        minHeight: 34, marginBottom: 2,
        opacity: locked ? 0.65 : 1
      }}
    >
      <span style={{ opacity: iconOpacity, display: 'flex', alignItems: 'center', flexShrink: 0, lineHeight: 0 }}>
        <NavIcon name={item.icon} color={iconColor} />
      </span>
      <span style={{ flex: 1 }}>{item.label}</span>
      {locked && gate && (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke={theme.textMuted} strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2"/>
            <path d="M7 11V7a5 5 0 0110 0v4"/>
          </svg>
          <span style={{ fontSize: 8, fontFamily: MONO, fontWeight: 700, color: theme.accent, background: theme.accentLight || theme.accent + '18', padding: '1px 5px', borderRadius: 3 }}>
            {gate.toUpperCase()}
          </span>
        </span>
      )}
      {SHORTCUTS[item.id] && (
        <span style={{ fontSize: 9, color: theme.textMuted, fontFamily: "'IBM Plex Mono', 'SF Mono', monospace" }}>
          {'\u2318' + SHORTCUTS[item.id]}
        </span>
      )}
      {statusDot && (
        <span
          title={statusTooltip || ''}
          style={{
            width: 6, height: 6, borderRadius: '50%',
            background: statusDot === 'green' ? '#10B981' : statusDot === 'amber' ? '#F59E0B' : '#EF4444',
            flexShrink: 0
          }}
        />
      )}
      {statusCount > 0 && (
        <span
          onClick={function(e) {
            e.stopPropagation();
            var _agId = item.agentId || DOMAIN_AGENT_MAP[item.id];
            if (_agId && onClick) onClick('/agent/' + _agId + '?section=alerts');
          }}
          style={{
            fontSize: 9, fontWeight: 700,
            background: theme.accent + '18',
            color: theme.accent,
            padding: '1px 5px', borderRadius: 8,
            fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
            minWidth: 16, textAlign: 'center',
            lineHeight: '14px', cursor: 'pointer'
          }}>
          {statusCount}
        </span>
      )}
      {item.badge && (
        <span style={{
          background: theme.critical || '#EF4444', color: '#fff',
          fontSize: 9, fontWeight: 700, padding: '1px 6px',
          borderRadius: 10, minWidth: 18, textAlign: 'center',
          lineHeight: '14px', display: 'inline-block',
          fontFamily: "'IBM Plex Mono', 'SF Mono', monospace"
        }}>!</span>
      )}
    </button>
  );
}

// Section label -- small uppercase header
function SectionLabel(props) {
  var theme = props.theme;
  return (
    <div style={{
      padding: '8px 12px 4px',
      fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: 1, color: theme.textMuted, fontFamily: FONT
    }}>
      {props.children}
    </div>
  );
}

function RecentThreads(props) {
  var theme = props.theme;
  var onSelect = props.onSelect;
  var agentFilter = props.agentFilter;

  var _threads = useState([]);
  var threads = _threads[0]; var setThreads = _threads[1];
  var _loading = useState(true);
  var loading = _loading[0]; var setLoading = _loading[1];
  var _editingId = useState(null); var editingId = _editingId[0]; var setEditingId = _editingId[1];
  var _editVal = useState(''); var editVal = _editVal[0]; var setEditVal = _editVal[1];
  var _channels = useState([]); var channels = _channels[0]; var setChannels = _channels[1];
  var _threadSearch = useState(''); var threadSearch = _threadSearch[0]; var setThreadSearch = _threadSearch[1];

  useEffect(function() {
    setLoading(true);
    var url = '/api/threads?limit=15';
    if (agentFilter) url += '&agent_id=' + agentFilter;
    fetch(url).then(function(r) { return r.json(); }).then(function(d) {
      setLoading(false);
      if (d.ok && d.data) {
        var items = Array.isArray(d.data.threads) ? d.data.threads : (Array.isArray(d.data.conversations) ? d.data.conversations : []);
        setThreads(items);
      }
    }).catch(function() { setLoading(false); });
  }, [agentFilter]);

  useEffect(function() {
    if (agentFilter) return;
    fetch('/api/threads/channels').then(function(r) { return r.ok ? r.json() : null; }).then(function(d) {
      if (d && d.ok && d.data && Array.isArray(d.data.channels)) setChannels(d.data.channels);
    }).catch(function() {});
  }, [agentFilter]);

  var filteredThreads = threads.filter(function(t) {
    if (!threadSearch) return true;
    var q = threadSearch.toLowerCase();
    return (t.title || '').toLowerCase().indexOf(q) >= 0;
  });

  if (loading) {
    return (
      <div style={{ padding: '8px 14px', fontSize: 11, color: theme.textMuted, fontFamily: 'IBM Plex Sans, sans-serif' }}>
        Loading threads...
      </div>
    );
  }

  if (!threads.length && !channels.length) {
    return (
      <div style={{ padding: '8px 14px', fontSize: 11, color: theme.textMuted, fontFamily: 'IBM Plex Sans, sans-serif', fontStyle: 'italic' }}>
        No threads yet. Start chatting with any agent.
      </div>
    );
  }

  return (
    <div style={{ padding: '0 4px', maxHeight: 260, overflow: 'auto' }}>
      {/* Search input */}
      <div style={{ padding: '4px 8px', marginBottom: 4 }}>
        <input
          value={threadSearch}
          onChange={function(e) { setThreadSearch(e.target.value); }}
          placeholder="Search threads..."
          style={{
            width: '100%', border: '1px solid ' + theme.border, borderRadius: 6,
            padding: '5px 8px', fontSize: 12, outline: 'none',
            background: theme.inputBg || 'transparent', color: theme.text,
            fontFamily: FONT, boxSizing: 'border-box'
          }}
        />
      </div>
      {/* Channels pinned at top */}
      {!agentFilter && channels.length > 0 && channels.map(function(ch) {
        var chTitle = ch.title || ('#' + ch.id);
        return (
          <div
            key={ch.id}
            onClick={function() { if (onSelect) onSelect('/agent/' + ch.agent_id + '?thread=' + ch.id); }}
            style={{ padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontFamily: FONT, marginBottom: 1 }}
            onMouseEnter={function(e) { e.currentTarget.style.background = theme.sidebarHover; }}
            onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.sidebarText, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {chTitle}
              <span style={{ fontSize: 10, color: theme.textMuted, marginLeft: 6, fontWeight: 400 }}>{ch.agent_name || ''}</span>
            </div>
          </div>
        );
      })}
      {!agentFilter && channels.length > 0 && <div style={{ height: 1, background: theme.border, margin: '4px 10px 4px' }} />}
      {/* Regular threads */}
      {filteredThreads.map(function(t) {
        var title = t.title || 'Thread';
        if (title.length > 28) title = title.slice(0, 25) + '...';
        var agentName = (t.agent_id || '').replace('agent_', '').replace(/_/g, ' ');
        return (
          <div
            key={t.id}
            onClick={function() { if (onSelect) onSelect('/agent/' + t.agent_id + '?thread=' + t.id); }}
            style={{ padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontFamily: FONT, color: theme.sidebarText, marginBottom: 1 }}
            onMouseEnter={function(e) { e.currentTarget.style.background = theme.sidebarHover; }}
            onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}
          >
            {editingId === t.id ? (
              <input
                value={editVal}
                onChange={function(e) { setEditVal(e.target.value); }}
                onBlur={function() {
                  if (editVal.trim() && t.agent_id) {
                    fetch('/api/agents/' + t.agent_id + '/threads/' + t.id, {
                      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ title: editVal.trim() })
                    }).then(function() {
                      setEditingId(null);
                      setThreads(function(prev) {
                        return prev.map(function(th) {
                          if (th.id === t.id) return Object.assign({}, th, { title: editVal.trim() });
                          return th;
                        });
                      });
                    }).catch(function() { setEditingId(null); });
                  } else {
                    setEditingId(null);
                  }
                }}
                onKeyDown={function(e) { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingId(null); }}
                onClick={function(e) { e.stopPropagation(); }}
                autoFocus
                style={{ fontSize: 11, fontFamily: FONT, width: '100%', border: '1px solid ' + (theme.accent || '#4F6EF7'), borderRadius: 4, padding: '2px 6px', outline: 'none', color: theme.sidebarText, background: 'transparent', boxSizing: 'border-box' }}
              />
            ) : (
              <div
                onDoubleClick={function(e) { e.stopPropagation(); setEditingId(t.id); setEditVal(t.title || ''); }}
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title="Double-click to rename"
              >
                {title}
              </div>
            )}
            {!agentFilter && <div style={{ fontSize: 9, color: theme.textMuted, marginTop: 1 }}>{agentName}</div>}
          </div>
        );
      })}
    </div>
  );
}

function inferArtifactType(art) {
  var at = (art.artifact_type || '').toLowerCase();
  if (at === 'letter' || at === 'letter_draft') return 'Letter';
  if (at === 'report' || at === 'analysis') return 'Report';
  if (at === 'email' || at === 'email_draft') return 'Email';
  if (at === 'session' || at === 'session_prompt') return 'Session';
  if (at === 'strategic_brief') return 'Brief';
  if (at === 'code' || at === 'script') return 'Code';
  if (at === 'workflow') return 'Work';
  var title = (art.title || '').toLowerCase();
  var body = (art.content_markdown || art.body || '').toLowerCase();
  if (title.indexOf('letter') >= 0 || body.indexOf('dear ') >= 0) return 'Letter';
  if (title.indexOf('report') >= 0 || title.indexOf('audit') >= 0) return 'Report';
  if (title.indexOf('brief') >= 0) return 'Brief';
  if (title.indexOf('email') >= 0) return 'Email';
  return 'Doc';
}

function RecentArtifacts(props) {
  var theme = props.theme;
  var onSelect = props.onSelect;
  var agentFilter = props.agentFilter || null;

  var _artifacts = useState([]);
  var artifacts = _artifacts[0]; var setArtifacts = _artifacts[1];
  var _loading = useState(true);
  var loading = _loading[0]; var setLoading = _loading[1];

  useEffect(function() {
    var url = '/api/artifacts?limit=15';
    if (agentFilter) url += '&agent=' + encodeURIComponent(agentFilter);
    fetch(url).then(function(r) { return r.ok ? r.json() : null; }).then(function(d) {
      setLoading(false);
      if (d && d.ok && Array.isArray(d.data.artifacts)) {
        setArtifacts(d.data.artifacts);
      }
    }).catch(function() { setLoading(false); });
  }, [agentFilter]);

  if (loading) {
    return React.createElement('div', {
      style: { padding: '8px 14px', fontSize: 11, color: theme.textMuted, fontFamily: FONT }
    }, 'Loading artifacts...');
  }

  if (artifacts.length === 0) {
    return React.createElement('div', {
      style: { padding: '8px 14px', fontSize: 11, color: theme.textMuted, fontFamily: FONT }
    }, 'No artifacts yet.');
  }

  var TYPE_COLORS = {
    'letter': '#8B5CF6', 'report': '#4F6EF7', 'scenario': '#F59E0B',
    'email': '#06B6D4', 'document': '#6B7280', 'session': '#E11D48',
    'code': '#8B5CF6', 'workflow': '#10B981'
  };

  return React.createElement('div', { style: { padding: '0 4px' } },
    artifacts.slice(0, 10).map(function(art) {
      var agentLabel = (art.agent_name || art.agent_id || '').replace('agent_', '').replace(/_/g, ' ');
      var type = inferArtifactType(art);
      var typeColor = TYPE_COLORS[type.toLowerCase()] || '#6B7280';
      var title = (art.title || 'Untitled').slice(0, 40);
      if ((art.title || '').length > 40) title += '...';
      return React.createElement('div', {
        key: art.id,
        onClick: function() {
          if (art.agent_id) {
            onSelect('/agent/' + art.agent_id + '?artifact=' + art.id);
          }
        },
        onMouseEnter: function(e) { e.currentTarget.style.background = theme.sidebarHover || 'rgba(0,0,0,0.04)'; },
        onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; },
        style: {
          padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 1
        }
      },
        React.createElement('div', {
          style: {
            width: 28, height: 28, borderRadius: 6, flexShrink: 0,
            background: typeColor + '15', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 9, fontWeight: 700, color: typeColor, fontFamily: MONO, textTransform: 'uppercase'
          }
        }, type.slice(0, 3)),
        React.createElement('div', { style: { flex: 1, minWidth: 0 } },
          React.createElement('div', {
            style: {
              fontSize: 12, color: theme.sidebarText, fontFamily: FONT,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
            }
          }, title),
          React.createElement('div', {
            style: { fontSize: 10, color: theme.textMuted, fontFamily: FONT }
          }, agentLabel)
        )
      );
    })
  );
}

export default function Sidebar(props) {
  var user = props.user;
  var prefs = props.prefs;
  var currentPath = props.currentPath;
  var onNavigate = props.onNavigate;
  var isMobile = props.isMobile;
  var open = props.open;
  var onClose = props.onClose;
  var onAskHub = props.onAskHub;
  var railMode = props.railMode || false;
  var theme = useTheme();
  var permsHook = usePermissions();

  var _sidebarStatus = useState({});
  var sidebarStatus = _sidebarStatus[0]; var setSidebarStatus = _sidebarStatus[1];
  var _hoverAgent = useState(null);
  var hoverAgent = _hoverAgent[0]; var setHoverAgent = _hoverAgent[1];

  // Force re-render when hash changes so active section highlights update
  var _hashVer = useState(0); var setHashVer = _hashVer[1];
  useEffect(function() {
    function onHashChange() { setHashVer(function(v) { return v + 1; }); }
    window.addEventListener('hashchange', onHashChange);
    return function() { window.removeEventListener('hashchange', onHashChange); };
  }, []);

  // Agent sort / pin / active-only state
  var _agentSort = useState((prefs && prefs.agent_sort_pref) || 'recent');
  var agentSort = _agentSort[0]; var setAgentSort = _agentSort[1];
  var _pinnedInit = [];
  try { _pinnedInit = JSON.parse((prefs && prefs.pinned_agents) || '[]'); } catch(e) {}
  var _pinnedAgents = useState(Array.isArray(_pinnedInit) ? _pinnedInit : []);
  var pinnedAgents = _pinnedAgents[0]; var setPinnedAgents = _pinnedAgents[1];
  var _hideInactive = useState((prefs && prefs.hide_inactive_agents != null) ? prefs.hide_inactive_agents === 1 : true);
  var hideInactive = _hideInactive[0]; var setHideInactive = _hideInactive[1];
  var _agentUsage = useState({});
  var agentUsage = _agentUsage[0]; var setAgentUsage = _agentUsage[1];
  var _sortDropdownOpen = useState(false);
  var sortDropdownOpen = _sortDropdownOpen[0]; var setSortDropdownOpen = _sortDropdownOpen[1];

  useEffect(function() {
    fetch('/api/threads?limit=100').then(function(r) { return r.ok ? r.json() : null; }).then(function(d) {
      if (!d || !d.ok || !d.data) return;
      var items = Array.isArray(d.data.threads) ? d.data.threads : [];
      var usage = {};
      for (var ti = 0; ti < items.length; ti++) {
        var t = items[ti];
        if (!t.agent_id) continue;
        if (!usage[t.agent_id]) usage[t.agent_id] = { last: t.last_message_at || t.updated_at || '', count: 0 };
        usage[t.agent_id].count += (t.message_count || 1);
        var tLast = t.last_message_at || t.updated_at || '';
        if (tLast > usage[t.agent_id].last) usage[t.agent_id].last = tLast;
      }
      setAgentUsage(usage);
    }).catch(function() {});
  }, []);

  // Plan gate: fetch current plan from billing subscription
  var _plan = useState('start'); var plan = _plan[0]; var setPlan = _plan[1];
  useEffect(function() {
    fetch('/api/billing/subscription')
      .then(function(r) { return r.json(); })
      .then(function(j) { if (j.ok && j.data && j.data.plan) setPlan(j.data.plan); })
      .catch(function() {});
  }, []);

  // Agent drill-in sections from D1 (fetched when drillAgentId changes)
  var _agentSections = useState(null);
  var agentSections = _agentSections[0]; var setAgentSections = _agentSections[1];

  // Custom sections (PD9): user-created, fetched per agent
  var _customSections = useState([]);
  var customSections = _customSections[0]; var setCustomSections = _customSections[1];

  // Storefront flip mode: null = normal, string = active storefront id
  var _storefrontMode = useState(null);
  var storefrontMode = _storefrontMode[0]; var setStorefrontMode = _storefrontMode[1];

  // All agents for Aaron testing mode -- as domain objects for NavItem rendering
  var _allAgentDomains = useState(null); var allAgentDomains = _allAgentDomains[0]; var setAllAgentDomains = _allAgentDomains[1];
  useEffect(function() {
    if (!user || user.email !== 'aaron@sprintmode.co') return;
    fetch('/api/agents').then(function(r) { return r.json(); }).then(function(j) {
      if (j.ok && j.data) {
        var agents = (Array.isArray(j.data) ? j.data : j.data.agents || [])
          .filter(function(a) { return a.active; })
          .sort(function(a, b) {
            var an = (a.display_name || a.name || '').toLowerCase();
            var bn = (b.display_name || b.name || '').toLowerCase();
            return an < bn ? -1 : an > bn ? 1 : 0;
          });
        var domains = agents.map(function(ag) {
          var existing = DOMAIN_MAP[ag.id] || AGENT_DISPLAY[ag.id];
          var icon = existing ? existing.icon : (ag.category === 'personal' ? 'hr-team' : ag.category === 'compliance' ? 'shield' : ag.category === 'fund_operations' ? 'bar' : ag.category === 'clones' ? 'people' : 'bolt');
          return {
            id: ag.id,
            label: labelFor(ag),
            icon: icon,
            route: '/agent/' + ag.id,
            agentId: ag.id
          };
        });
        // Pin Hub first, HC-Builder second (for Aaron)
        var pinned = [];
        var unpinned = [];
        for (var di = 0; di < domains.length; di++) {
          if (domains[di].id === 'agent_hub') {
            pinned.unshift(domains[di]);
          } else if (domains[di].id === 'agent_hc_builder') {
            pinned.push(domains[di]);
          } else {
            unpinned.push(domains[di]);
          }
        }
        setAllAgentDomains(pinned.concat(unpinned));
      }
    }).catch(function() {});
  }, [user]);

  // Detect agent drill-in state early so useEffect can depend on it
  var _agentDrillMatch = currentPath ? currentPath.match(/^\/agent\/([^\/]+)/) : null;
  var _drillAgentIdEarly = _agentDrillMatch ? _agentDrillMatch[1] : null;

  useEffect(function() {
    if (!_drillAgentIdEarly) { setAgentSections(null); setCustomSections([]); setStorefrontMode(null); return; }
    fetch('/api/agents/' + _drillAgentIdEarly)
      .then(function(r) { return r.json(); })
      .then(function(j) {
        if (j.ok && j.data) {
          var ag = j.data.agent || j.data;
          var parsed = null;
          try { parsed = JSON.parse(ag.sidebar_sections || 'null'); } catch(e) {}
          if (parsed && Array.isArray(parsed.sections)) {
            var STALE_IDS = ['alerts', 'overview', 'threads'];
            var cleaned = parsed.sections.filter(function(s) { return STALE_IDS.indexOf(s.id) < 0; });
            setAgentSections(cleaned);
          } else {
            setAgentSections(null);
          }
        }
      }).catch(function() {});
    // Fetch custom sections for this agent
    fetch('/api/sidebar-sections?agent_id=' + _drillAgentIdEarly)
      .then(function(r) { return r.json(); })
      .then(function(j) {
        if (j.ok && j.data) {
          setCustomSections(Array.isArray(j.data.sections) ? j.data.sections : []);
        }
      }).catch(function() {});
  }, [_drillAgentIdEarly]);

  useEffect(function() {
    function fetchStatus() {
      fetch('/api/brief/sidebar-status')
        .then(function(r) { return r.json(); })
        .then(function(data) {
          if (data && data.ok && data.data) {
            var statusMap = {};
            (Array.isArray(data.data) ? data.data : []).forEach(function(s) {
              statusMap[s.agent_id] = s;
            });
            setSidebarStatus(statusMap);
          }
        })
        .catch(function() {});
    }
    fetchStatus();
    // Poll every 60s so badges update without page reload
    var iv = setInterval(fetchStatus, 60000);
    function handleRefresh() { fetchStatus(); }
    window.addEventListener('hub-impersonate-changed', handleRefresh);
    return function() {
      clearInterval(iv);
      window.removeEventListener('hub-impersonate-changed', handleRefresh);
    };
  }, []);

  // Detect agent drill-in state from current URL
  var agentDrillMatch = currentPath ? currentPath.match(/^\/agent\/([^\/]+)/) : null;
  var drillAgentId = agentDrillMatch ? agentDrillMatch[1] : null;
  var isAgentDrillIn = !!drillAgentId;

  // Detect storefront drill-in
  var isStorefrontDrillIn = currentPath === '/studios-public';

  // Detect Jockey drill-in
  var isJockeyDrillIn = !isAgentDrillIn && currentPath && currentPath.startsWith('/jockey');

  var allDomains = parseDomains(prefs);
  var logoAccent = theme.accent || '#4F6EF7';

  function handleNav(route) {
    onNavigate(route);
    if (isMobile && onClose) onClose();
  }

  function handleAskHub() {
    if (isMobile && onClose) onClose();
    if (onAskHub) onAskHub();
  }

  function handleNewThread() {
    var targetAgent = drillAgentId || '';
    if (!targetAgent) {
      // Not in agent view -- try last-used from threads, fall back to EM
      fetch('/api/threads?limit=1').then(function(r) { return r.json(); }).then(function(d) {
        var fallback = 'agent_engineering_manager';
        if (d.ok && d.data) {
          var items = Array.isArray(d.data.threads) ? d.data.threads : [];
          if (items.length > 0 && items[0].agent_id) fallback = items[0].agent_id;
        }
        window.location.href = '/agent/' + fallback + '?newThread=true';
      }).catch(function() {
        window.location.href = '/agent/agent_engineering_manager?newThread=true';
      });
      return;
    }
    fetch('/api/agents/' + targetAgent + '/threads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: 'New thread', visibility: 'shared' })
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (d.ok && d.data && d.data.id) {
        window.location.href = '/agent/' + targetAgent + '?thread=' + d.data.id;
      } else {
        window.location.href = '/agent/' + targetAgent + '?newThread=true';
      }
    }).catch(function() {
      window.location.href = '/agent/' + targetAgent + '?newThread=true';
    });
  }

  var sidebarStyle = isMobile ? {
    position: 'fixed', top: 0, left: 0, width: 220, height: '100vh',
    background: theme.sidebar, borderRight: '1px solid ' + theme.sidebarBorder,
    display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden',
    zIndex: 1000,
    transform: open ? 'translateX(0)' : 'translateX(-220px)',
    transition: 'transform 0.25s ease',
    boxShadow: open ? '4px 0 24px rgba(0,0,0,0.12)' : 'none'
  } : {
    width: 220, height: '100vh', background: theme.sidebar, borderRight: '1px solid ' + theme.sidebarBorder,
    display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden'
  };

  // ---- Logo area (shared across states) ----
  var logoArea = (
    <div
      onClick={function() { handleNav('/brief'); }}
      style={{
        height: 51, padding: '0 14px', display: 'flex', alignItems: 'center', gap: 10,
        borderBottom: '1px solid ' + theme.sidebarBorder, flexShrink: 0,
        cursor: 'pointer'
      }}
    >
      <HubLogo size={28} color={logoAccent} />
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: theme.sidebarTextActive, fontFamily: FONT, lineHeight: 1.2 }}>Hub</div>
        <div style={{ fontSize: 9.5, fontWeight: 500, color: theme.sidebarText, fontFamily: FONT, lineHeight: 1.4 }}>{(user && user.company && user.company.name) || 'Sprint Mode'}</div>
      </div>
    </div>
  );

  // ---- Bottom bar (shared) ----
  var bottomBar = (
    <div style={{ flexShrink: 0, borderTop: '1px solid ' + theme.sidebarBorder }}>
      {user && user.email === 'aaron@sprintmode.co' && (
        <div style={{ padding: '4px 8px' }}>
          <NavItem
            item={{ id: 'analytics', label: 'Cost Analytics', icon: 'bar', route: '/analytics' }}
            active={isActive('/analytics', currentPath)}
            onClick={handleNav}
            theme={theme}
          />
        </div>
      )}
    </div>
  );

  // ================================================================
  // AGENT DRILL-IN STATE
  // ================================================================
  if (isAgentDrillIn) {
    var drillDisplay = AGENT_DISPLAY[drillAgentId] || { label: drillAgentId, icon: 'bolt' };
    var drillStorefrontIds = AGENT_STOREFRONT_MAP[drillAgentId] || [];
    var drillStorefronts = drillStorefrontIds.map(function(sfId) {
      return STOREFRONTS.filter(function(s) { return s.id === sfId; })[0];
    }).filter(Boolean);

    // Storefront flip: show teal-accented storefront sub-view
    if (storefrontMode) {
      var sfObj = STOREFRONTS.filter(function(s) { return s.id === storefrontMode; })[0] || { label: storefrontMode, color: '#14B8A6' };
      var sfAccent = sfObj.color || '#14B8A6';
      var sfSectionsFlip = STOREFRONT_SIDEBAR_SECTIONS[storefrontMode] || [];
      var sfHashSec = getHashSection();
      var sfActiveSec = sfHashSec || (sfSectionsFlip.length > 0 ? sfSectionsFlip[0].id : '');
      return (
        <>
          {isMobile && open && (
            <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999 }} />
          )}
          <div style={sidebarStyle} className="hub-sidebar">
            {logoArea}
            <div style={{ padding: '8px 8px 0', flexShrink: 0 }}>
              <button
                onClick={function() { setStorefrontMode(null); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '7px 12px', borderRadius: 8, border: 'none',
                  cursor: 'pointer', background: 'transparent',
                  color: theme.textMuted, fontSize: 12, fontFamily: FONT, textAlign: 'left'
                }}
              >
                <NavIcon name="arrow-left" color={theme.textMuted} />
                Back to {drillDisplay.label}
              </button>
            </div>
            <div style={{
              padding: '8px 14px 10px',
              borderBottom: '1px solid ' + theme.sidebarBorder, flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ opacity: 0.8 }}>
                  <NavIcon name="briefcase" color={sfAccent} />
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: theme.sidebarTextActive, fontFamily: FONT }}>
                  {sfObj.label}
                </span>
              </div>
              <div style={{ fontSize: 9.5, color: sfAccent, marginTop: 2, fontFamily: FONT, fontWeight: 500 }}>Storefront</div>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '6px 8px' }}>
              {sfSectionsFlip.map(function(s) {
                var sfRoute = '/agent/' + drillAgentId + '?sf=' + storefrontMode + '&section=' + s.id;
                var sfActive = currentPath === '/agent/' + drillAgentId && sfActiveSec === s.id;
                return (
                  <NavItem
                    key={s.id}
                    item={{ id: s.id, label: s.label, icon: s.icon, route: sfRoute }}
                    active={sfActive}
                    onClick={handleNav}
                    theme={Object.assign({}, theme, { accent: sfAccent })}
                  />
                );
              })}
            </div>
            <div style={{ flexShrink: 0, borderTop: '1px solid ' + theme.sidebarBorder }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px 2px' }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={theme.sidebarText} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: theme.textMuted, fontFamily: FONT }}>Threads</span>
                <span style={{ fontSize: 9, color: theme.textMuted, fontFamily: FONT, opacity: 0.5, marginLeft: 'auto' }}>&#8984;&#8679;O</span>
                <div
                  onClick={function(e) { e.stopPropagation(); handleNewThread(); }}
                  title="New thread (Cmd+Shift+O)"
                  style={{
                    cursor: 'pointer', padding: '0 4px', borderRadius: 4, fontSize: 14, fontWeight: 300,
                    color: theme.textMuted, lineHeight: '16px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                  onMouseEnter={function(e) { e.currentTarget.style.color = theme.accent || '#4F6EF7'; }}
                  onMouseLeave={function(e) { e.currentTarget.style.color = theme.textMuted; }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                </div>
              </div>
              <div style={{ padding: '0 4px' }}>
                <RecentThreads theme={theme} onSelect={handleNav} agentFilter={drillAgentId} onNewThread={handleNewThread} />
              </div>
            </div>
            {bottomBar}
          </div>
        </>
      );
    }

    var hashSec = getHashSection();
    var activeSec = hashSec || 'threads';
    var systemSections = AGENT_SIDEBAR_SECTIONS[drillAgentId] || agentSections || AGENT_SIDEBAR_SECTIONS._default;

    return (
      <>
        {isMobile && open && (
          <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999 }} />
        )}
        <div style={sidebarStyle} className="hub-sidebar">
          {logoArea}

          {/* Back button */}
          <div style={{ padding: '8px 8px 0', flexShrink: 0 }}>
            <button
              onClick={function() { handleNav('/brief'); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '7px 12px', borderRadius: 8, border: 'none',
                cursor: 'pointer', background: 'transparent',
                color: theme.textMuted, fontSize: 12, fontFamily: FONT,
                textAlign: 'left'
              }}
            >
              <NavIcon name="arrow-left" color={theme.textMuted} />
              Back to Hub
            </button>
          </div>

          {/* Agent header */}
          <div style={{
            padding: '8px 14px 10px',
            borderBottom: '1px solid ' + theme.sidebarBorder,
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ opacity: 0.8 }}>
                <NavIcon name={drillDisplay.icon} color={theme.accent} />
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: theme.sidebarTextActive, fontFamily: FONT }}>
                {drillDisplay.label}
              </span>
            </div>
          </div>

          {/* Scrollable zone container */}
          <div style={{ flex: 1, overflow: 'auto', padding: '6px 8px' }}>

            {/* ZONE 1: System Sections */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '6px 4px 2px', gap: 4 }}>
              <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: theme.textMuted, fontFamily: FONT, flex: 1 }}>Sections</span>
              <span title="Organization-level. Same for all users with access.">
                <NavIcon name="lock" color={theme.textMuted} />
              </span>
            </div>
            {systemSections.map(function(s) {
              var sectionRoute = s.id === 'overview'
                ? '/agent/' + drillAgentId
                : '/agent/' + drillAgentId + '?section=' + s.id;
              var sectionActive = activeSec === s.id && currentPath === '/agent/' + drillAgentId;
              return (
                <NavItem
                  key={s.id}
                  item={{ id: s.id, label: s.label, icon: s.icon, route: sectionRoute }}
                  active={sectionActive}
                  onClick={handleNav}
                  theme={theme}
                />
              );
            })}

            {/* ZONE 2: Custom Sections */}
            {customSections.length > 0 && (
              <>
                <div style={{ margin: '8px 4px 0', borderTop: '1px solid ' + theme.sidebarBorder }} />
                <div style={{ display: 'flex', alignItems: 'center', padding: '6px 4px 2px', gap: 4 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#7C3AED', fontFamily: FONT, flex: 1 }}>Custom</span>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
                  </svg>
                </div>
                {customSections.map(function(cs) {
                  var csRoute = '/agent/' + drillAgentId + '?section=custom_' + cs.id;
                  var isShared = cs.is_shared === 1 || cs.is_shared === true;
                  var isOwn = cs.created_by === (user && user.email);
                  return (
                    <div
                      key={cs.id}
                      onClick={function() { handleNav(csRoute); }}
                      onMouseEnter={function(e) { e.currentTarget.style.background = theme.sidebarHover; }}
                      onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                        padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
                        color: theme.sidebarText, fontSize: 12.5, fontFamily: FONT,
                        marginBottom: 2, background: 'transparent'
                      }}
                    >
                      <span style={{ opacity: 0.5, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        {isShared ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={theme.sidebarText} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                            <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
                          </svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={theme.sidebarText} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                          </svg>
                        )}
                      </span>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cs.title}</span>
                      {!isOwn && <span style={{ fontSize: 9, color: '#7C3AED', fontFamily: FONT }}>shared</span>}
                    </div>
                  );
                })}
              </>
            )}

            {/* ZONE 3: Storefronts */}
            {drillStorefronts.length > 0 && (
              <>
                <div style={{ margin: '8px 4px 0', borderTop: '1px solid ' + theme.sidebarBorder }} />
                <div style={{ padding: '6px 4px 2px', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#0D9488', fontFamily: FONT }}>
                  Storefronts
                </div>
                {drillStorefronts.map(function(sf) {
                  return (
                    <button
                      key={sf.id}
                      onClick={function() { setStorefrontMode(sf.id); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                        padding: '7px 12px', borderRadius: 8, border: 'none',
                        cursor: 'pointer', background: 'transparent',
                        color: theme.sidebarText, fontSize: 12.5, fontFamily: FONT,
                        textAlign: 'left', marginBottom: 2
                      }}
                      onMouseEnter={function(e) { e.currentTarget.style.background = theme.sidebarHover; }}
                      onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{ opacity: 0.5, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <NavIcon name="briefcase" color="#0D9488" />
                      </span>
                      <span style={{ flex: 1 }}>{sf.label}</span>
                    </button>
                  );
                })}
              </>
            )}

            {/* Agent Settings */}
            <div style={{ margin: '8px 4px 0', borderTop: '1px solid ' + theme.sidebarBorder }} />
            <NavItem
              item={{ id: 'agent_settings', label: 'Agent Settings', icon: 'settings', route: '/agent/' + drillAgentId + '?section=agent_settings' }}
              active={activeSec === 'agent_settings' && currentPath === '/agent/' + drillAgentId}
              onClick={handleNav}
              theme={theme}
            />

            {/* Artifacts zone */}
            <div style={{ margin: '8px 4px 0', borderTop: '1px solid ' + theme.sidebarBorder }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 4px 2px' }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={theme.sidebarText} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="18" rx="2"/><path d="M8 7h8M8 12h6"/>
              </svg>
              <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: theme.textMuted, fontFamily: FONT }}>Artifacts</span>
            </div>
            <RecentArtifacts theme={theme} onSelect={handleNav} agentFilter={drillAgentId} />

            {/* Divider before threads */}
            <div style={{ margin: '8px 4px 0', borderTop: '1px solid ' + theme.sidebarBorder }} />

            {/* ZONE 4: Threads */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 4px 2px' }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={theme.sidebarText} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: theme.textMuted, fontFamily: FONT }}>Threads</span>
              <span style={{ fontSize: 9, color: theme.textMuted, fontFamily: FONT, opacity: 0.5, marginLeft: 'auto' }}>&#8984;&#8679;O</span>
              <div
                onClick={function(e) { e.stopPropagation(); handleNewThread(); }}
                title="New thread (Cmd+Shift+O)"
                style={{
                  cursor: 'pointer', padding: '0 4px', borderRadius: 4, fontSize: 14, fontWeight: 300,
                  color: theme.textMuted, lineHeight: '16px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
                onMouseEnter={function(e) { e.currentTarget.style.color = theme.accent || '#4F6EF7'; }}
                onMouseLeave={function(e) { e.currentTarget.style.color = theme.textMuted; }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </div>
            </div>
            <RecentThreads theme={theme} onSelect={handleNav} agentFilter={drillAgentId} onNewThread={handleNewThread} />

          </div>

          {bottomBar}
        </div>
      </>
    );
  }

  // ================================================================
  // STOREFRONT DRILL-IN STATE
  // ================================================================
  if (isStorefrontDrillIn) {
    var sfSections = [
      { label: 'Dashboard',  icon: 'bar',       route: '/studios-public' },
      { label: 'Clients',    icon: 'crm',       route: '/clients' },
      { label: 'Billing',    icon: 'dollar',    route: '/agent/agent_bookkeeper' },
      { label: 'Outreach',   icon: 'outbox',    route: '/agent/agent_sdr' },
      { label: 'Analytics',  icon: 'eye',       route: '/agent/agent_cfo' },
    ];
    return (
      <>
        {isMobile && open && (
          <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999 }} />
        )}
        <div style={sidebarStyle} className="hub-sidebar">
          {logoArea}
          <div style={{ padding: '8px 8px 0', flexShrink: 0 }}>
            <button
              onClick={function() { handleNav('/brief'); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '7px 12px', borderRadius: 8, border: 'none',
                cursor: 'pointer', background: 'transparent',
                color: theme.textMuted, fontSize: 12, fontFamily: FONT,
                textAlign: 'left'
              }}
            >
              <NavIcon name="arrow-left" color={theme.textMuted} />
              Back to Hub
            </button>
          </div>
          <div style={{
            padding: '8px 14px 10px',
            borderBottom: '1px solid ' + theme.sidebarBorder,
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ opacity: 0.8 }}>
                <NavIcon name="briefcase" color={theme.accent} />
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: theme.sidebarTextActive, fontFamily: FONT }}>Studios</span>
            </div>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: '6px 8px' }}>
            {sfSections.map(function(s) {
              return (
                <NavItem
                  key={s.label}
                  item={s}
                  active={currentPath === s.route}
                  onClick={handleNav}
                  theme={theme}
                />
              );
            })}
          </div>
          {bottomBar}
        </div>
      </>
    );
  }

  // ================================================================
  // JOCKEY DRILL-IN STATE
  // ================================================================
  if (isJockeyDrillIn) {
    var jockeySections = [
      { label: 'Console',   icon: 'queue',     route: '/jockey' },
      { label: 'Dashboard', icon: 'bar',       route: '/jockey/dashboard' },
      { label: 'Skills',    icon: 'bolt',      route: '/jockey/skills' },
      { label: 'Patterns',  icon: 'brief',     route: '/jockey/patterns' },
      { label: 'Dev',       icon: 'ledger',    route: '/jockey/dev' },
      { label: 'Team',      icon: 'people',    route: '/jockey/team' },
      { label: 'PRDs',      icon: 'folder',    route: null },
      { label: 'Artifacts', icon: 'queue',     route: '/work' },
    ];
    return (
      <>
        {isMobile && open && (
          <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999 }} />
        )}
        <div style={sidebarStyle} className="hub-sidebar">
          {logoArea}
          <div style={{ padding: '8px 8px 0', flexShrink: 0 }}>
            <button
              onClick={function() { handleNav('/brief'); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '7px 12px', borderRadius: 8, border: 'none',
                cursor: 'pointer', background: 'transparent',
                color: theme.textMuted, fontSize: 12, fontFamily: FONT,
                textAlign: 'left'
              }}
            >
              <NavIcon name="arrow-left" color={theme.textMuted} />
              Back to Hub
            </button>
          </div>
          <div style={{
            padding: '8px 14px 10px',
            borderBottom: '1px solid ' + theme.sidebarBorder,
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ opacity: 0.8 }}>
                <NavIcon name="jockey" color={theme.accent} />
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: theme.sidebarTextActive, fontFamily: FONT }}>Jockey</span>
            </div>
            <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 3, fontFamily: FONT }}>Engineering Workspace</div>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: '6px 8px' }}>
            {jockeySections.map(function(s) {
              if (!s.route) {
                return (
                  <button key={s.label} disabled style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                    padding: '8px 12px', borderRadius: 8, border: 'none',
                    cursor: 'default', background: 'transparent',
                    color: theme.textMuted, fontSize: 12.5, fontFamily: FONT,
                    textAlign: 'left', opacity: 0.4, minHeight: 34, marginBottom: 2
                  }}>
                    <span style={{ opacity: 0.5, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                      <NavIcon name={s.icon} color={theme.textMuted} />
                    </span>
                    <span style={{ flex: 1 }}>{s.label}</span>
                    <span style={{ fontSize: 9, color: theme.textMuted }}>soon</span>
                  </button>
                );
              }
              return (
                <NavItem
                  key={s.label}
                  item={s}
                  active={currentPath === s.route || (s.route === '/jockey' && currentPath === '/jockey')}
                  onClick={handleNav}
                  theme={theme}
                />
              );
            })}
          </div>
          {bottomBar}
        </div>
      </>
    );
  }

  // ================================================================
  // HOME STATE
  // ================================================================

  // Filter domains by permissions (or Aaron testing mode: all agents)
  var visibleDomains;
  if (user && user.email === 'aaron@sprintmode.co' && allAgentDomains) {
    var adminDomain = DOMAIN_MAP['admin'];
    // Exclude agents already in nav (from sidebar_order_json / parseDomains)
    var navAgentIds = allDomains.map(function(d) { return d.agentId; }).filter(Boolean);
    var extraAgents = allAgentDomains.filter(function(d) { return navAgentIds.indexOf(d.agentId || d.id) < 0; });
    var seen = {};
    var deduped = allDomains.concat(extraAgents).filter(function(d) {
      if (!d) return false;
      if (SIDEBAR_EXCLUDE.indexOf(d.id) >= 0) return false;
      if (seen[d.route]) return false;
      seen[d.route] = true;
      return true;
    });
    visibleDomains = deduped.concat([adminDomain]).filter(Boolean);
  } else {
    visibleDomains = allDomains.filter(function(item) {
      if (!item) return false;
      if (item.id === 'admin') return false;
      if (SIDEBAR_EXCLUDE.indexOf(item.id) >= 0) return false;
      if (!item.agentId) return true; // no permission filter for non-agent items
      return permsHook.canView(item.agentId);
    });
  }

  // Sort + filter helpers
  function sortAgents(agents, mode) {
    var sorted = agents.slice();
    if (mode === 'alpha') {
      sorted.sort(function(a, b) { return (a.label || '').localeCompare(b.label || ''); });
    } else if (mode === 'recent') {
      sorted.sort(function(a, b) {
        var aLast = agentUsage[a.id] ? agentUsage[a.id].last : '';
        var bLast = agentUsage[b.id] ? agentUsage[b.id].last : '';
        return bLast.localeCompare(aLast);
      });
    } else if (mode === 'most_used') {
      sorted.sort(function(a, b) {
        var aC = agentUsage[a.id] ? agentUsage[a.id].count : 0;
        var bC = agentUsage[b.id] ? agentUsage[b.id].count : 0;
        return bC - aC;
      });
    } else if (mode === 'category') {
      sorted.sort(function(a, b) { return (a.category || '').localeCompare(b.category || ''); });
    }
    var pinned = sorted.filter(function(a) { return pinnedAgents.indexOf(a.id) >= 0; });
    var unpinned = sorted.filter(function(a) { return pinnedAgents.indexOf(a.id) < 0; });
    return pinned.concat(unpinned);
  }
  function filterAgents(agents) {
    if (!hideInactive) return agents;
    var cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    return agents.filter(function(a) {
      var u = agentUsage[a.id];
      return u && u.last > cutoff;
    });
  }

  // Status counts for section label
  var _activeCount = Object.values(sidebarStatus).filter(function(s) { return s && s.status === 'green'; }).length;
  var _needsCount = Object.values(sidebarStatus).filter(function(s) { return s && (s.status === 'red' || s.status === 'amber'); }).length;
  var sectionLabel = 'Your Agents' + (_activeCount > 0 ? (' - ' + _activeCount + ' active') : '') + (_needsCount > 0 ? (', ' + _needsCount + ' need you') : '');
  var displayDomains = sortAgents(filterAgents(visibleDomains), agentSort);
  // C26-S17v1: Hub always first in agent list
  var _hubItems = displayDomains.filter(function(d) { return d.id === 'hub' || d.agentId === 'agent_hub'; });
  var _nonHubItems = displayDomains.filter(function(d) { return d.id !== 'hub' && d.agentId !== 'agent_hub'; });
  displayDomains = _hubItems.concat(_nonHubItems);

  // ---- RAIL MODE (48px icons only) ----
  if (railMode) {
    return (
      <div style={{
        width: 48, height: '100vh',
        background: theme.sidebar,
        borderRight: '1px solid ' + theme.sidebarBorder,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', paddingTop: 10,
        overflow: 'hidden', flexShrink: 0
      }}>
        {/* Hub logo */}
        <div
          style={{ marginBottom: 12, cursor: 'pointer' }}
          onClick={function() { handleNav('/brief'); }}
          title="Brief"
        >
          <HubLogo size={20} color={theme.accent || '#4F6EF7'} />
        </div>

        {/* Agent icons */}
        {displayDomains.map(function(item) {
          var isActive = currentPath === item.route ||
            (item.agentId && currentPath === ('/agent/' + item.agentId));
          return (
            <div
              key={item.id}
              onClick={function() { handleNav(item.route || ('/agent/' + item.agentId)); }}
              title={item.label}
              style={{
                width: 32, height: 32, borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', marginBottom: 2, flexShrink: 0,
                background: isActive ? (theme.sidebarActiveBg || '#EEF2FF') : 'transparent'
              }}
            >
              <NavIcon
                name={item.icon || 'circle'}
                color={isActive ? (theme.accent || '#4F6EF7') : (theme.textMuted || '#94A3B8')}
              />
            </div>
          );
        })}

        <div style={{ flex: 1 }} />

        {/* Settings icon at bottom */}
        <div
          onClick={function() { handleNav('/settings'); }}
          title="Settings"
          style={{
            width: 32, height: 32, borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', marginBottom: 12, flexShrink: 0
          }}
        >
          <NavIcon name="gear" color={theme.textMuted || '#94A3B8'} />
        </div>
      </div>
    );
  }

  return (
    <>
      {isMobile && open && (
        <div
          onClick={onClose}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999 }}
        />
      )}
      <div style={sidebarStyle} className="hub-sidebar">
        {logoArea}

        {/* Section label + sort dropdown */}
        <div style={{ padding: '10px 10px 4px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: theme.textMuted, fontFamily: FONT }}>
            {sectionLabel}
          </div>
          <div style={{ position: 'relative' }}>
            <button onClick={function() { setSortDropdownOpen(!sortDropdownOpen); }} style={{
              background: 'none', border: 'none', cursor: 'pointer', fontSize: 10,
              color: theme.textMuted, padding: '2px 4px', fontFamily: FONT
            }}>
              {agentSort === 'recent' ? 'Recent' : agentSort === 'most_used' ? 'Most Used' : agentSort === 'alpha' ? 'A-Z' : 'Category'}
              {' '}{'\u25BE'}
            </button>
            {sortDropdownOpen && (
              <div style={{
                position: 'absolute', right: 0, top: 22, background: theme.cardBg || theme.surface || '#fff',
                border: '1px solid ' + (theme.border || '#e2e8f0'), borderRadius: 8,
                padding: 4, zIndex: 200, minWidth: 116, boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}>
                {['recent', 'most_used', 'alpha', 'category'].map(function(opt) {
                  return (
                    <div key={opt} onClick={function() {
                      setAgentSort(opt);
                      setSortDropdownOpen(false);
                      fetch('/api/me/preferences', {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ agent_sort_pref: opt })
                      });
                    }} style={{
                      padding: '6px 10px', fontSize: 12, cursor: 'pointer', borderRadius: 4,
                      fontWeight: agentSort === opt ? 700 : 400,
                      color: agentSort === opt ? '#4F6EF7' : theme.text,
                      fontFamily: FONT
                    }}>
                      {opt === 'recent' ? 'Recent' : opt === 'most_used' ? 'Most Used' : opt === 'alpha' ? 'A-Z' : 'Category'}
                    </div>
                  );
                })}
                <div style={{ height: 1, background: theme.border || '#e2e8f0', margin: '4px 0' }} />
                <div onClick={function() {
                  var next = !hideInactive;
                  setHideInactive(next);
                  setSortDropdownOpen(false);
                  fetch('/api/me/preferences', {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ hide_inactive_agents: next ? 1 : 0 })
                  });
                }} style={{
                  padding: '6px 10px', fontSize: 12, cursor: 'pointer', borderRadius: 4,
                  color: hideInactive ? '#4F6EF7' : theme.text, fontFamily: FONT,
                  fontWeight: hideInactive ? 700 : 400
                }}>
                  {hideInactive ? '\u2713 ' : ''}Active Only
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Agents section */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '0 8px' }}>
          {displayDomains.map(function(item) {
            var agentId = DOMAIN_AGENT_MAP[item.id] || item.agentId;
            var status = agentId ? sidebarStatus[agentId] : null;
            var dot = status ? status.status : null;
            var _crit = status ? (status.critical_count || 0) : 0;
            var _att = status ? (status.attention_count || 0) : 0;
            var count = _crit + _att;
            var _tip = dot === 'green' ? 'Healthy' : dot === 'amber' ? (_att + ' item' + (_att !== 1 ? 's' : '') + ' need attention') : dot === 'red' ? (_crit > 0 ? (_crit + ' critical item' + (_crit !== 1 ? 's' : '')) : 'No runs in 48h') : '';
            var popoutAgentId = item.agentId || agentId;
            var _itemGate = PLAN_GATES[item.id] || null;
            var _itemLocked = false; // C26-S17v1: lock icons removed for Sprint Mode (internal)
            return (
              <div
                key={item.id}
                style={{ position: 'relative' }}
                onMouseEnter={function() { setHoverAgent(item.id); }}
                onMouseLeave={function() { setHoverAgent(null); }}
              >
                <NavItem
                  item={item}
                  active={isActive(item.route, currentPath)}
                  onClick={handleNav}
                  theme={theme}
                  statusDot={dot}
                  statusCount={count > 0 ? count : null}
                  statusTooltip={_tip}
                  locked={_itemLocked}
                  gate={_itemGate}
                  onPin={item.agentId ? function(id) {
                    var newPinned = pinnedAgents.indexOf(id) >= 0
                      ? pinnedAgents.filter(function(p) { return p !== id; })
                      : pinnedAgents.concat([id]);
                    setPinnedAgents(newPinned);
                    fetch('/api/me/preferences', {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ pinned_agents: JSON.stringify(newPinned) })
                    });
                  } : null}
                  isPinned={item.agentId ? pinnedAgents.indexOf(item.id) >= 0 : false}
                />
                {hoverAgent === item.id && popoutAgentId && (
                  <div
                    onClick={function(e) {
                      e.stopPropagation();
                      var popoutUrl = window.location.origin + '/agent/' + popoutAgentId + '?popout=true';
                      window.open(popoutUrl, 'hub_popout_' + popoutAgentId, 'width=480,height=700,resizable=yes,scrollbars=yes');
                    }}
                    title="Open in floating window"
                    style={{
                      position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                      cursor: 'pointer', padding: '2px 5px', borderRadius: 4,
                      opacity: 0.5, zIndex: 10
                    }}
                    onMouseEnter={function(e) { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = theme.sidebarHover || 'rgba(0,0,0,0.06)'; }}
                    onMouseLeave={function(e) { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.background = 'transparent'; }}
                  >
                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                      <path d="M8 2h4v4M12 2L7 7M5 3H3a1 1 0 00-1 1v7a1 1 0 001 1h7a1 1 0 001-1V9" stroke={theme.textMuted || '#64748b'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </div>
            );
          })}

          {/* Storefronts section */}
          {STOREFRONTS.length > 0 && (
            <>
              <div style={{ margin: '8px 4px', borderTop: '1px solid ' + theme.sidebarBorder }} />
              <SectionLabel theme={theme}>Storefronts</SectionLabel>
              {STOREFRONTS.map(function(sf) {
                return (
                  <NavItem
                    key={sf.id}
                    item={sf}
                    active={isActive(sf.route, currentPath)}
                    onClick={handleNav}
                    theme={theme}
                  />
                );
              })}
            </>
          )}
        </div>

        {/* Artifacts section */}
        <div style={{ flexShrink: 0, maxHeight: '25%', overflowY: 'auto', borderTop: '1px solid ' + theme.sidebarBorder }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={theme.sidebarText} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="18" rx="2"/><path d="M8 7h8M8 12h6"/>
            </svg>
            <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: theme.textMuted, fontFamily: FONT }}>
              Artifacts
            </span>
          </div>
          <RecentArtifacts theme={theme} onSelect={handleNav} agentFilter={null} />
        </div>

        {/* Threads section */}
        <div style={{ flexShrink: 0, maxHeight: '30%', overflowY: 'auto', borderTop: '1px solid ' + theme.sidebarBorder }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px 2px' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={theme.sidebarText} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: theme.textMuted, fontFamily: FONT }}>
              Threads
            </span>
            <span style={{ fontSize: 9, color: theme.textMuted, fontFamily: FONT, opacity: 0.5, marginLeft: 'auto' }}>&#8984;&#8679;O</span>
            <div
              onClick={function(e) { e.stopPropagation(); handleNewThread(); }}
              title="New thread (Cmd+Shift+O)"
              style={{
                cursor: 'pointer', padding: '0 4px', borderRadius: 4, fontSize: 14, fontWeight: 300,
                color: theme.textMuted, lineHeight: '16px',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
              onMouseEnter={function(e) { e.currentTarget.style.color = theme.accent || '#4F6EF7'; }}
              onMouseLeave={function(e) { e.currentTarget.style.color = theme.textMuted; }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </div>
          </div>
          <RecentThreads theme={theme} onSelect={handleNav} agentFilter={null} onNewThread={handleNewThread} />
        </div>

        {bottomBar}
      </div>
    </>
  );
}
